import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { TaxAuthorityConfig, SiiEmisor, AccountingConfig } from "../types";

interface UseTaxAuthorityConfigResult {
  config: TaxAuthorityConfig | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  updateConfig: (data: UpdateConfigData) => Promise<TaxAuthorityConfig>;
  isUpdating: boolean;
}

interface UpdateConfigData {
  emisor?: SiiEmisor;
  certificateP12?: string;
  certificatePassword?: string;
  siiWebRut?: string;
  siiWebPassword?: string;
  environment?: "certification" | "production";
  accountingConfig?: AccountingConfig;
}

export type { UpdateConfigData };

/**
 * Hook to fetch and update tax authority configuration.
 */
export function useTaxAuthorityConfig(): UseTaxAuthorityConfigResult {
  const [config, setConfig] = useState<TaxAuthorityConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchConfig = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<{ config: TaxAuthorityConfig | null }>(
          "/api/tax-authority",
        );
        setConfig(data.config);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setConfig(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  const updateConfig = useCallback(
    async (data: UpdateConfigData): Promise<TaxAuthorityConfig> => {
      setIsUpdating(true);
      try {
        const result = await apiFetch.post<{ config: TaxAuthorityConfig }>(
          "/api/tax-authority",
          data,
        );
        setConfig(result.config);
        return result.config;
      } finally {
        setIsUpdating(false);
      }
    },
    [],
  );

  return {
    config,
    isLoading,
    error,
    refetch,
    updateConfig,
    isUpdating,
  };
}
